import React, { useEffect, useState } from 'react';
import { TextField, InputAdornment, SxProps, Theme } from '@mui/material';
import { Search, X } from 'lucide-react';

export interface SearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  onDebounceChange?: (value: string) => void;
  debounceMs?: number;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  disabled?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value: controlledValue,
  onChange,
  onDebounceChange,
  debounceMs = 300,
  placeholder = 'Search...',
  className = '',
  fullWidth = true,
  size = 'small',
  sx,
  autoFocus = false,
  disabled = false,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(controlledValue ?? '');

  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue ?? '');
    }
  }, [controlledValue, isControlled]);

  const onDebounceChangeRef = React.useRef(onDebounceChange);
  useEffect(() => {
    onDebounceChangeRef.current = onDebounceChange;
  }, [onDebounceChange]);

  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (!onDebounceChangeRef.current) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const handler = setTimeout(() => {
      onDebounceChangeRef.current?.(internalValue);
    }, debounceMs);

    return () => clearTimeout(handler);
  }, [internalValue, debounceMs]);

  const handleChange = (newVal: string) => {
    if (!isControlled) {
      setInternalValue(newVal);
    }
    onChange?.(newVal);
  };

  const handleClear = () => {
    handleChange('');
  };

  const currentValue = isControlled ? controlledValue : internalValue;

  return (
    <TextField
      size={size}
      fullWidth={fullWidth}
      placeholder={placeholder}
      value={currentValue}
      onChange={(e) => handleChange(e.target.value)}
      autoComplete="off"
      autoFocus={autoFocus}
      disabled={disabled}
      className={className}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search size={16} color="#8698a6" />
          </InputAdornment>
        ),
        endAdornment: currentValue ? (
          <InputAdornment position="end">
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              style={{
                background: 'none',
                border: 'none',
                color: '#8698a6',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2px',
                borderRadius: '4px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)')
              }
              onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = '#8698a6')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          color: 'var(--text-primary)',
          backgroundColor: 'var(--bg-input)',
          borderRadius: '6px',
          '& fieldset': {
            borderColor: 'var(--border-color)',
          },
          '&:hover fieldset': {
            borderColor: 'var(--text-secondary)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--accent-blue)',
          },
          '&.Mui-disabled fieldset': {
            borderColor: 'var(--border-color)',
          },
        },
        ...sx,
      }}
    />
  );
};

export default SearchBar;
